/**
 * Competitive positioning scatter/bubble chart.
 *
 * AC-010: Interactive 2D scatter chart using Recharts
 * AC-011: Default axes (Revenue x Employees)
 * AC-012: Dropdown to change axes
 * AC-013: Hover tooltip
 * AC-014: Click navigates to company detail
 * EC-006: Hidden when no numeric data available
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { PositioningDataPoint, CompanyComparisonColumn } from '../../types';

interface PositioningMapProps {
  data: PositioningDataPoint[];
  columns: CompanyComparisonColumn[];
}

// Available dimensions for axis selection
const AXIS_OPTIONS = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'employee_count', label: 'Employee Count' },
  { value: 'market_cap', label: 'Market Cap' },
  { value: 'net_income', label: 'Net Income' },
  { value: 'founded_year', label: 'Founded Year' },
  { value: 'kb_completeness', label: 'KB Completeness' },
  { value: 'product_count', label: 'Product Count' },
];

function formatAxisValue(value: number, metricKey: string): string {
  if (metricKey === 'kb_completeness') {
    return `${Math.round(value * 100)}%`;
  }
  if (metricKey === 'founded_year') {
    return String(Math.round(value));
  }
  if (value >= 1_000_000_000_000) return `$${(value / 1_000_000_000_000).toFixed(1)}T`;
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return String(Math.round(value));
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: {
      company_name: string;
      company_type: string;
      x: number | null;
      y: number | null;
    };
  }>;
  xLabel: string;
  yLabel: string;
  xKey: string;
  yKey: string;
}

function CustomTooltip({ active, payload, xLabel, yLabel, xKey, yKey }: CustomTooltipProps) {
  if (!active || !payload || !payload[0]) return null;
  const data = payload[0].payload;

  const COMPANY_TYPE_LABELS: Record<string, string> = {
    public: 'Public',
    pe_backed: 'PE-Backed',
    private: 'Private',
    subsidiary: 'Subsidiary',
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
      <div className="font-semibold text-gray-900">{data.company_name}</div>
      <div className="text-xs text-gray-500">
        {COMPANY_TYPE_LABELS[data.company_type] || data.company_type}
      </div>
      <div className="mt-2 space-y-1 text-sm">
        <div>
          {xLabel}: {data.x != null ? formatAxisValue(data.x, xKey) : 'N/A'}
        </div>
        <div>
          {yLabel}: {data.y != null ? formatAxisValue(data.y, yKey) : 'N/A'}
        </div>
      </div>
    </div>
  );
}

export function PositioningMap({ data, columns }: PositioningMapProps) {
  const navigate = useNavigate();
  const [xAxis, setXAxis] = useState('revenue');
  const [yAxis, setYAxis] = useState('employee_count');

  // Build chart data from columns and selected axes
  const getMetricValue = useCallback((col: CompanyComparisonColumn, key: string): number | null => {
    const metric = col.metrics[key];
    if (!metric || metric.value === null || metric.value === undefined) return null;
    if (typeof metric.value === 'number') return metric.value;
    return null;
  }, []);

  const chartData = columns.map((col) => {
    const matchingPoint = data.find((p) => p.company_id === col.company_id);
    return {
      company_id: col.company_id,
      company_name: col.company_name,
      company_type: col.company_type,
      x: getMetricValue(col, xAxis),
      y: getMetricValue(col, yAxis),
      z: matchingPoint?.bubble_size || 50,
      color: matchingPoint?.color || '#3B82F6',
    };
  });

  // EC-006: Hide when no data available for both axes
  const hasAnyData = chartData.some((d) => d.x !== null || d.y !== null);
  if (!hasAnyData) return null;

  // Filter out points with null values for rendering
  const validData = chartData.filter((d) => d.x !== null && d.y !== null);

  const xLabel = AXIS_OPTIONS.find((o) => o.value === xAxis)?.label || xAxis;
  const yLabel = AXIS_OPTIONS.find((o) => o.value === yAxis)?.label || yAxis;

  return (
    <div data-testid="positioning-map">
      <div className="flex items-center gap-4 mb-4">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
          Competitive Positioning
        </h3>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">X:</label>
          <select
            value={xAxis}
            onChange={(e) => setXAxis(e.target.value)}
            className="text-xs border border-gray-300 rounded px-2 py-1"
            data-testid="x-axis-select"
          >
            {AXIS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <label className="text-xs text-gray-500 ml-2">Y:</label>
          <select
            value={yAxis}
            onChange={(e) => setYAxis(e.target.value)}
            className="text-xs border border-gray-300 rounded px-2 py-1"
            data-testid="y-axis-select"
          >
            {AXIS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <XAxis
              type="number"
              dataKey="x"
              name={xLabel}
              tickFormatter={(v: number) => formatAxisValue(v, xAxis)}
              scale={xAxis === 'revenue' || xAxis === 'market_cap' ? 'log' : 'auto'}
              domain={['auto', 'auto']}
              label={{ value: xLabel, position: 'bottom', offset: 0 }}
            />
            <YAxis
              type="number"
              dataKey="y"
              name={yLabel}
              tickFormatter={(v: number) => formatAxisValue(v, yAxis)}
              label={{ value: yLabel, angle: -90, position: 'insideLeft' }}
            />
            <ZAxis dataKey="z" range={[100, 1000]} />
            <Tooltip
              content={<CustomTooltip xLabel={xLabel} yLabel={yLabel} xKey={xAxis} yKey={yAxis} />}
            />
            <Scatter
              data={validData}
              onClick={(point: { company_id?: string }) => {
                if (point?.company_id) {
                  navigate(`/companies/${point.company_id}`);
                }
              }}
              cursor="pointer"
            >
              {validData.map((entry, index) => (
                <Cell key={index} fill={entry.color} fillOpacity={0.7} stroke={entry.color} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
